import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { DOMSerializer, DOMParser, Fragment } from 'prosemirror-model';
import DOMPurify from 'dompurify';
import { processPlugins } from '../plugins';
import { EMPTY_OBJECT } from '../../../constants/Constants';
import EditorEvents from '../EditorEvents';

/**
 * @description Converts editor's raw input into html
 * @param {Array|string} content - Raw content of editor (Either JSON string or array)
 * @param {string[]} activePlugins - Array of plugin names to be enabled
 * @param {Object[]} pluginOptions - Object contains custom config options for plugins where the key is plugin name
 */
export const convertToHtml = (data, activePlugins, pluginOptions, marks) => {
  try {
    const editorId = Math.random().toString(36).substring(7);
    const events = new EditorEvents(editorId);
    const editorContent = typeof data === 'string' ? JSON.parse(data) : data;
    const {
      schema
    } = processPlugins([...activePlugins, 'placeholder'], pluginOptions, editorId, events, marks);
    const serializer = DOMSerializer.fromSchema(schema);
    const doc = Fragment.fromJSON(schema, editorContent);
    const fragment = serializer.serializeFragment(doc);
    const div = document.createElement('div');
    div.appendChild(DOMPurify.sanitize(fragment));
    return DOMPurify.sanitize(div.innerHTML);
  } catch (e) {
    return DOMPurify.sanitize(data);
  }
};

/**
 * @description Converts editor's raw input into html
 * @param {Array|string} content - Raw content of editor (Either JSON string or array)
 * @param {Object[]} schema - Editor schema instance
 */
export const convertToHtmlFromSchema = (schema, data) => {
  try {
    const editorContent = typeof data === 'string' ? JSON.parse(data) : data;
    const serializer = DOMSerializer.fromSchema(schema);
    const doc = Fragment.fromJSON(schema, editorContent);
    const fragment = serializer.serializeFragment(doc);
    return DOMPurify.sanitize(fragment);
  } catch (e) {
    return DOMPurify.sanitize(data);
  }
};

/**
 * @description Returns position of the given node if that is selected, this is usefull for showing
 * floating toolbars based on the selected node
 * @param {Object} state - Editor's state object
 * @param {string} nodeName - Node name as mentioned in schema
 */
export const getSelectedNodePosition = (state, nodeName) => {
  const currentNode = state.schema.nodes[nodeName];
  const node = state.doc.nodeAt(state.selection.$from.pos);
  return node && node.type === currentNode ? state.selection.$from.pos : null;
};

/**
 * @description Removed the current node selected
 * @param {Object} state - Editor's state object
 * @param {Function} dispatch - Function used to update the editor's state
 * @param {Object} pluginState - Plugin's state which contains information about position
 */
export const removeSelectedNodeByPlugin = (state, dispatch, view, pluginState) => {
  const {
    pos
  } = pluginState;
  if (pos !== null) {
    const node = state.doc.nodeAt(pos);
    if (dispatch) {
      const tr = state.tr.delete(pos, pos + node.nodeSize);
      dispatch(tr);
    }
    return true;
  }
  return false;
};

/**
 * @description Update selected node's attrs
 * @param {Object} state - Editor's state object
 * @param {Function} dispatch - Function used to update the editor's state
 * @param {Object} view - Editor's view object
 * @param {Object} pluginState - Plugin's state which contains information about position
 * @param {Object} attrs - Object of attributes to be updated in node
 */
export const updateSelectedNodeAttrs = function (state, dispatch, view, pluginState) {
  let attrs = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : EMPTY_OBJECT;
  const {
    pos
  } = pluginState || {};
  if (pos === null) {
    return false;
  }
  const node = state.doc.nodeAt(pos);
  const tr = state.tr.setNodeMarkup(pos, null, _objectSpread(_objectSpread({}, node.attrs), attrs));
  dispatch(tr);
  return false;
};
export const setAttr = (attrs, value, name) => {
  const result = {};
  for (const prop in attrs) result[prop] = attrs[prop];
  result[name] = value;
  return result;
};
export const getDocFromHtml = function (schema, content) {
  let parseOptions = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {
    preserveWhitespace: true
  };
  const element = document.createElement('div');
  element.innerHTML = DOMPurify.sanitize(content.trim());
  return DOMParser.fromSchema(schema).parse(element, parseOptions);
};